import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertTriangle, CloudRain, Thermometer, Wind, Droplets, Eye, FileText, Bell, BellOff } from "lucide-react";
import LiveRadar from "@/components/weather/LiveRadar";
import { toast } from "sonner";
import { getAccurateLocation } from "@/lib/geolocate";

function severityColor(sev) {
  const s = (sev || "").toLowerCase();
  if (s === "extreme") return "hsl(0 84% 60%)";
  if (s === "severe") return "hsl(24 95% 53%)";
  if (s === "moderate") return "hsl(45 93% 47%)";
  return "hsl(199 89% 48%)";
}

function fmtIssued(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// Severe alert chime — short two-tone tactical beep
function playSevereChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const tones = [
      { freq: 880, start: 0, dur: 0.18 },
      { freq: 660, start: 0.22, dur: 0.18 },
      { freq: 880, start: 0.44, dur: 0.28 },
    ];
    tones.forEach((t) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = t.freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + t.start);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + t.start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t.start + t.dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + t.start);
      osc.stop(ctx.currentTime + t.start + t.dur + 0.02);
    });
  } catch { /* ignore */ }
}

export default function WeatherPage() {
  const [loc, setLoc] = useState(null);
  const [current, setCurrent] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [hourly, setHourly] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBlocked, setPushBlocked] = useState(false);
  const seenAlertIds = useRef(new Set());

  // Detect existing notification permission state on mount
  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      setPushBlocked(true);
      return;
    }
    if (Notification.permission === "granted") setPushEnabled(true);
    if (Notification.permission === "denied") setPushBlocked(true);
  }, []);

  useEffect(() => {
    getAccurateLocation({
      onSuccess: ({ lat, lon }) => setLoc({ lat, lon }),
      // Weather needs SOME location to render — fall back to NYC only on
      // real failure (denied / unavailable), not silently on stale IP data.
      onError: () => setLoc({ lat: 40.7128, lon: -74.006 }),
    });
  }, []);

  // Initial fetch + 60s alert polling
  useEffect(() => {
    if (!loc) return;
    let cancelled = false;
    const fetchAll = () => {
      api.get("/weather/current", { params: loc }).then((r) => !cancelled && setCurrent(r.data)).catch(() => {});
      api.get("/weather/forecast", { params: { ...loc, kind: "daily" } }).then((r) => !cancelled && setForecast(r.data)).catch(() => {});
      api.get("/weather/forecast", { params: { ...loc, kind: "hourly" } }).then((r) => !cancelled && setHourly(r.data)).catch(() => {});
    };
    const fetchAlerts = () => api.get("/weather/alerts", { params: loc }).then((r) => !cancelled && setAlerts(r.data)).catch(() => {});
    fetchAll();
    fetchAlerts();
    const t = setInterval(fetchAlerts, 60 * 1000);
    const t2 = setInterval(fetchAll, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); clearInterval(t2); };
  }, [loc]);

  // Detect NEW severe/extreme alerts and fire push + chime
  useEffect(() => {
    const feats = alerts?.features || [];
    let firstRun = seenAlertIds.current.size === 0;
    const newSevere = [];
    for (const a of feats) {
      if (!a.id || seenAlertIds.current.has(a.id)) continue;
      seenAlertIds.current.add(a.id);
      const sev = (a.properties?.severity || "").toLowerCase();
      if (!firstRun && (sev === "severe" || sev === "extreme")) {
        newSevere.push(a);
      }
    }
    if (newSevere.length === 0) return;
    playSevereChime();
    for (const a of newSevere) {
      const p = a.properties || {};
      toast.error(`${(p.severity || "").toUpperCase()} • ${p.event}`, {
        description: p.headline || p.areaDesc,
        duration: 12000,
      });
      if (pushEnabled && "Notification" in window && Notification.permission === "granted") {
        try {
          new Notification(`[${(p.severity || "").toUpperCase()}] ${p.event}`, {
            body: p.headline || p.areaDesc || "",
            tag: a.id,
            requireInteraction: (p.severity || "").toLowerCase() === "extreme",
          });
        } catch { /* ignore */ }
      }
    }
  }, [alerts, pushEnabled]);

  const enablePush = async () => {
    if (!("Notification" in window)) {
      toast.error("This browser doesn't support notifications");
      return;
    }
    if (Notification.permission === "granted") {
      setPushEnabled(true);
      playSevereChime();
      toast.success("Severe-weather push enabled");
      return;
    }
    if (Notification.permission !== "denied") {
      const res = await Notification.requestPermission();
      if (res === "granted") {
        setPushEnabled(true);
        setPushBlocked(false);
        playSevereChime();
        toast.success("Severe-weather push enabled");
      } else {
        setPushBlocked(res === "denied");
        toast.error("Notifications blocked by browser");
      }
    } else {
      setPushBlocked(true);
      toast.error("Notifications blocked. Update browser site settings to enable.");
    }
  };

  const obs = current?.observation?.properties;
  const periods = forecast?.properties?.periods || [];
  const activeCount = alerts?.features?.length || 0;
  const severeCount = (alerts?.features || []).filter((a) => ["severe", "extreme"].includes((a.properties?.severity || "").toLowerCase())).length;
  const fetchedAtLabel = obs?.timestamp ? `Obs ${fmtIssued(obs.timestamp)}` : "";

  return (
    <div className="absolute inset-0 flex flex-col" data-testid="weather-page">
      {/* Top bar */}
      <div className="h-12 px-4 flex items-center justify-between border-b border-border bg-card/80 backdrop-blur shrink-0">
        <div className="flex items-center gap-3">
          <div className="font-mono text-xs text-primary tracking-widest">// WEATHER OPERATIONS CENTER</div>
          <Badge variant="outline" className="font-mono text-[10px]">
            {activeCount} ALERT{activeCount === 1 ? "" : "S"}
          </Badge>
          {severeCount > 0 && (
            <Badge style={{ background: severityColor("severe"), color: "#fff" }} className="font-mono text-[10px] uppercase animate-pulse" data-testid="severe-badge">
              {severeCount} SEVERE
            </Badge>
          )}
        </div>
        <Button
          size="sm" variant={pushEnabled ? "default" : "outline"}
          onClick={enablePush} data-testid="push-toggle-btn"
          disabled={pushBlocked && !pushEnabled}
          className={`font-mono tracking-widest text-xs ${pushBlocked && !pushEnabled ? "opacity-60" : ""}`}
        >
          {pushEnabled ? <Bell className="w-3.5 h-3.5 mr-1.5" /> : <BellOff className="w-3.5 h-3.5 mr-1.5" />}
          {pushEnabled ? "PUSH: ON" : pushBlocked ? "PUSH BLOCKED" : "ENABLE SEVERE PUSH"}
        </Button>
      </div>

      {/* Big full-bleed radar */}
      <div className="flex-1 relative min-h-0">
        <LiveRadar lat={loc?.lat} lon={loc?.lon} height="100%" />
      </div>

      {/* Weather info panel BELOW the radar (scrollable, fixed height like Active Incidents) */}
      <div className="h-80 border-t border-border bg-card/95 backdrop-blur shrink-0 flex flex-col">
        <div className="h-10 px-4 flex items-center justify-between border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="font-mono text-xs tracking-widest text-primary">// LOCAL CONDITIONS &amp; FORECAST</div>
            <Badge variant="outline" className="font-mono text-[10px]">NWS</Badge>
          </div>
          <div className="font-mono text-[10px] text-muted-foreground">
            {fetchedAtLabel}
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="grid lg:grid-cols-3 gap-4 p-4">
            {/* Current conditions */}
            <div className="border border-border rounded-sm p-3 space-y-3" data-testid="current-conditions-card">
              <div className="font-mono text-[10px] text-muted-foreground tracking-widest flex items-center gap-2">
                <CloudRain className="w-3 h-3" /> CURRENT CONDITIONS
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-4xl font-bold font-mono" data-testid="current-temp">
                    {obs?.temperature?.value != null ? Math.round((obs.temperature.value * 9) / 5 + 32) : "--"}°F
                  </div>
                  <div className="text-xs text-muted-foreground">{obs?.textDescription || "—"}</div>
                  <div className="text-[10px] text-muted-foreground font-mono mt-1">{current?.station?.name || ""}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1.5"><Droplets className="w-3.5 h-3.5 text-primary" /> {obs?.relativeHumidity?.value != null ? Math.round(obs.relativeHumidity.value) : "--"}%</div>
                <div className="flex items-center gap-1.5"><Wind className="w-3.5 h-3.5 text-primary" /> {obs?.windSpeed?.value != null ? Math.round(obs.windSpeed.value * 0.621371) : "--"} mph</div>
                <div className="flex items-center gap-1.5"><Eye className="w-3.5 h-3.5 text-primary" /> {obs?.visibility?.value != null ? Math.round(obs.visibility.value / 1609) : "--"} mi</div>
                <div className="flex items-center gap-1.5"><Thermometer className="w-3.5 h-3.5 text-primary" /> {obs?.dewpoint?.value != null ? Math.round((obs.dewpoint.value * 9) / 5 + 32) : "--"}°F</div>
              </div>
            </div>

            {/* Active alerts */}
            <div className="border border-border rounded-sm p-3 space-y-2" data-testid="alerts-card">
              <div className="font-mono text-[10px] text-muted-foreground tracking-widest flex items-center gap-2">
                <AlertTriangle className="w-3 h-3" /> ACTIVE NWS ALERTS ({activeCount})
              </div>
              {activeCount === 0 && (
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  No active alerts in your service area.
                </div>
              )}
              {(alerts?.features || []).slice(0, 4).map((a) => {
                const p = a.properties || {};
                return (
                  <div key={a.id} className="border-l-4 pl-2 py-0.5 text-xs" style={{ borderColor: severityColor(p.severity) }}>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge style={{ background: severityColor(p.severity), color: "#fff" }} className="uppercase text-[9px] h-4 px-1">
                        {p.severity || "?"}
                      </Badge>
                      <span className="font-semibold">{p.event}</span>
                    </div>
                    <div className="text-muted-foreground line-clamp-2">{p.headline || p.areaDesc}</div>
                  </div>
                );
              })}
              {activeCount > 4 && (
                <div className="text-[10px] text-muted-foreground font-mono">+ {activeCount - 4} more…</div>
              )}
            </div>

            {/* Forecast notes for the area */}
            <div className="border border-border rounded-sm p-3 space-y-2" data-testid="local-notes-card">
              <div className="font-mono text-[10px] text-muted-foreground tracking-widest flex items-center gap-2">
                <FileText className="w-3 h-3" /> LOCAL FORECAST NOTES
              </div>
              {periods.slice(0, 3).map((p) => (
                <div key={p.number} className="border-l-2 border-primary/40 pl-2 py-0.5 text-xs">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{p.name}</span>
                    <Badge variant="outline" className="font-mono text-[9px] h-4">
                      {p.temperature}°{p.temperatureUnit} · {p.windDirection} {p.windSpeed}
                    </Badge>
                  </div>
                  <div className="text-muted-foreground leading-snug line-clamp-3">{p.detailedForecast}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 7-day extended forecast — moved up to replace the AFD block */}
          <div className="px-4 pb-4">
            <div className="border border-border rounded-sm p-3" data-testid="extended-forecast">
              <div className="font-mono text-[10px] text-muted-foreground tracking-widest flex items-center gap-2 mb-3">
                <FileText className="w-3 h-3" /> 7-DAY EXTENDED FORECAST
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
                {periods.slice(0, 14).map((p) => (
                  <div key={p.number} className="border border-border/60 rounded-sm p-2 text-xs">
                    <div className="font-mono text-[10px] text-primary tracking-widest line-clamp-1">{p.name}</div>
                    <div className="text-2xl font-bold mt-1">{p.temperature}°{p.temperatureUnit}</div>
                    <div className="text-[10px] text-muted-foreground line-clamp-3 mt-1 leading-snug">{p.shortForecast}</div>
                    <div className="font-mono text-[10px] text-muted-foreground mt-1.5">{p.windDirection} {p.windSpeed}</div>
                  </div>
                ))}
              </div>
              {!periods.length && (
                <div className="text-xs text-muted-foreground">Forecast unavailable.</div>
              )}
            </div>

            {/* Hourly compact strip */}
            <div className="mt-4 border border-border rounded-sm p-3">
              <div className="font-mono text-[10px] text-muted-foreground tracking-widest mb-2">NEXT 12 HOURS</div>
              <div className="flex gap-2 overflow-x-auto">
                {(hourly?.properties?.periods || []).slice(0, 12).map((p) => (
                  <div key={p.number} className="border border-border/60 rounded-sm p-2 shrink-0 w-20 text-center">
                    <div className="font-mono text-[10px] text-muted-foreground">{new Date(p.startTime).toLocaleTimeString([], { hour: "2-digit" })}</div>
                    <div className="text-base font-bold">{p.temperature}°</div>
                    <div className="text-[10px] text-muted-foreground line-clamp-2">{p.shortForecast}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
